import { useVoiceCallContext } from "@/v5/contexts/VoiceCallContext";
import { CallerIdPicker, useCallerIdChoice } from "@/v5/components/CallerIdPicker";
import { Avatar, AvatarFallback } from "@/v5/components/ui/avatar";
import { Button } from "@/v5/components/ui/button";
import { cn } from "@/v5/lib/utils";
import { useAnyModalOpen } from "@/components/ui/Modal";
import { useIsBelow } from "@/hooks/useBreakpoint";
import {
  Mic,
  MicOff,
  Phone,
  PhoneCall,
  Maximize2,
  Minimize2,
  X,
  Smartphone,
  Monitor,
} from "lucide-react";

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatPhoneNumber(raw?: string | null): string {
  if (!raw) return "No phone number";
  const digits = raw.replace(/[^0-9]/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

function DeviceLabel({ type }: { type: "browser" | "phone" | "sip" | null }) {
  switch (type) {
    case "phone":
      return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Smartphone className="h-3.5 w-3.5" /> via Handset
        </span>
      );
    case "sip":
      return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Monitor className="h-3.5 w-3.5" /> via IP Phone
        </span>
      );
    default:
      return (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Phone className="h-3.5 w-3.5" /> via Browser
        </span>
      );
  }
}

export function FloatingVoiceCall() {
  const vc = useVoiceCallContext();

  // Step aside for an open modal, on the phone tier only. See FloatingNoteForm for the
  // full reasoning: z-[9999] corner chrome sits exactly on a phone sheet's action row,
  // Modal.tsx names these widgets as the case its registry exists for, and hiding beats
  // unmounting so a live call keeps its state. Desktop is untouched (R2).
  //
  // Two separate unconditional calls, never `useAnyModalOpen() && useIsBelow("md")` —
  // that short-circuits and changes this component's hook count when a modal opens
  // (React #310). FloatingNoteForm carries the full note.
  const anyModalOpen = useAnyModalOpen();
  const isPhone = useIsBelow("md");
  const stepAsideForModal = anyModalOpen && isPhone;
  // Before the early return, so the hook count never changes.
  const callerId = useCallerIdChoice(vc.isOpen ? vc.activePatientId : null);

  if (!vc.isOpen && vc.callStatus === "idle") return null;

  const patientName = vc.activePatientName || "Patient";
  const initials = patientName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "PT";

  const isMinimized = vc.mode === "minimized";
  const isIdle = vc.callStatus === "idle";

  // ── Minimized: compact card at bottom-left (only during active call) ──
  if (isMinimized && !isIdle) {
    const statusLabel =
      vc.callStatus === "connecting"
        ? "Connecting..."
        : vc.callStatus === "ringing"
          ? "Ringing..."
          : "Connected";

    const statusColor =
      vc.callStatus === "connected"
        ? "text-green-500"
        : "text-blue-500";

    return (
      <div className={cn("fixed bottom-6 left-6 z-[9999] w-[300px] rounded-2xl bg-card border border-border shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300", stepAsideForModal && "hidden")}>
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={cn(
                  "h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0",
                  vc.callStatus === "connected"
                    ? "bg-green-100 text-green-700"
                    : "bg-blue-100 text-blue-700",
                )}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{patientName}</p>
                <div className="flex items-center gap-1.5">
                  <div
                    className={cn(
                      "h-1.5 w-1.5 rounded-full flex-shrink-0",
                      vc.callStatus === "connected" ? "bg-green-500" : "bg-blue-500 animate-pulse",
                    )}
                  />
                  <span className={cn("text-[11px]", statusColor)}>{statusLabel}</span>
                </div>
              </div>
            </div>
            {vc.callStatus === "connected" && (
              <span className="text-xs font-mono text-muted-foreground tabular-nums flex-shrink-0">
                {formatDuration(vc.callDuration)}
              </span>
            )}
          </div>

          <div className="flex items-center justify-end gap-1.5">
            {vc.deviceType === "browser" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={vc.toggleMute}
                className={cn(
                  "h-8 w-8 rounded-full",
                  vc.isMuted ? "bg-red-100 text-red-600 hover:bg-red-200" : "hover:bg-muted",
                )}
              >
                {vc.isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={vc.expand}
              className="h-8 w-8 rounded-full hover:bg-muted"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={vc.hangUp}
              className="h-8 w-8 rounded-full bg-red-500 hover:bg-red-600 text-white"
            >
              <Phone className="h-3.5 w-3.5 rotate-135" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Expanded: centered overlay ────────────────────────────────────────
  const handleMakeCall = async () => {
    if (!vc.activePatientPhone || !vc.activePatientId) return;
    if (!vc.isReady) {
      // `initialize()` sets errorMessage on the way out, which renders below.
      // Bail only after that has had its chance — never silently.
      const ready = await vc.initialize();
      if (!ready) return;
    }
    const digits = vc.activePatientPhone.replace(/[^0-9]/g, "");
    await vc.makeCall(
      digits,
      vc.activePatientId,
      vc.activePatientName ?? undefined,
      vc.activePatientPhone,
      callerId.callerNumber,
    );
  };

  const handleClose = () => {
    if (vc.isOnCall) {
      vc.minimize();
    } else {
      vc.closeCallPanel();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="relative w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center gap-6 p-8">
          {/* Close / Minimize button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 rounded-full"
            title={vc.isOnCall ? "Minimize call" : "Close"}
          >
            {vc.isOnCall ? <Minimize2 className="h-4 w-4" /> : <X className="h-4 w-4" />}
          </Button>

          {/* Avatar */}
          <div
            className={cn(
              "rounded-full transition-all",
              isIdle
                ? ""
                : vc.callStatus === "connected"
                  ? "ring-4 ring-green-200"
                  : "ring-4 ring-blue-200 animate-pulse",
            )}
          >
            <Avatar className="h-24 w-24">
              <AvatarFallback
                className={cn(
                  "text-2xl font-bold",
                  isIdle
                    ? "bg-muted text-muted-foreground"
                    : vc.callStatus === "connected"
                      ? "bg-green-100 text-green-700"
                      : "bg-blue-100 text-blue-700",
                )}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Name + phone */}
          <div className="text-center">
            <p className="text-lg font-semibold">{patientName}</p>
            <p className="text-sm text-muted-foreground">
              {formatPhoneNumber(vc.activePatientPhone)}
            </p>
            {vc.deviceType && <DeviceLabel type={vc.deviceType} />}
          </div>

          {/* Idle state: "Call Patient" button */}
          {isIdle && (
            <div className="flex flex-col items-center gap-3 w-full">
              <CallerIdPicker
                options={callerId.options}
                value={callerId.selected}
                onChange={callerId.setSelected}
                loadFailed={callerId.loadFailed}
              />
              <Button
                onClick={handleMakeCall}
                disabled={(!vc.isReady && !vc.errorMessage) || vc.isInitializing || !vc.activePatientPhone || callerId.loading}
                className="w-full max-w-[240px] gap-2 bg-green-600 hover:bg-green-700 text-white h-12 text-base"
              >
                <PhoneCall className="h-5 w-5" />
                Call {patientName}
              </Button>
              {!vc.isReady && vc.isInitializing && (
                <p className="text-xs text-muted-foreground animate-pulse">
                  Initializing voice...
                </p>
              )}
              {/*
                Not gated on `!isReady`. A call that fails on the microphone or
                on media leaves the device perfectly registered — isReady stays
                true — so gating on it here is what kept the reason invisible.
              */}
              {!vc.isInitializing && vc.errorMessage && (
                <p className="text-xs text-destructive text-center">
                  {vc.errorMessage}
                </p>
              )}
            </div>
          )}

          {/* Active states */}
          {vc.callStatus === "connecting" && (
            <div className="flex items-center gap-2 text-blue-600">
              <div className="h-3 w-3 rounded-full bg-blue-500 animate-ping" />
              <span className="text-sm font-medium">Connecting...</span>
            </div>
          )}

          {vc.callStatus === "ringing" && (
            <div className="flex items-center gap-2 text-blue-600">
              <PhoneCall className="h-4 w-4 animate-bounce" />
              <span className="text-sm font-medium">Ringing...</span>
            </div>
          )}

          {vc.callStatus === "connected" && (
            <div className="flex items-center gap-2">
              <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
              <span className="text-sm font-medium text-green-600">Connected</span>
              <span className="text-sm tabular-nums text-muted-foreground font-mono">
                {formatDuration(vc.callDuration)}
              </span>
            </div>
          )}

          {/* Active call controls */}
          {vc.isOnCall && (
            <div className="flex items-center gap-3">
              {vc.deviceType === "browser" && (
                <Button
                  variant={vc.isMuted ? "destructive" : "outline"}
                  size="icon"
                  onClick={vc.toggleMute}
                  className="h-12 w-12 rounded-full"
                  title={vc.isMuted ? "Unmute" : "Mute"}
                >
                  {vc.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </Button>
              )}

              <Button
                variant="destructive"
                size="icon"
                onClick={vc.hangUp}
                className="h-14 w-14 rounded-full"
                title="End Call"
              >
                <Phone className="h-6 w-6 rotate-135" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
